import { useMemo } from "react";

import { TextField as MUITextField } from "@mui/material";
import MuiAutocomplete, {
  createFilterOptions,
} from "@mui/material/Autocomplete";
import {
  AutocompleteProps,
  fieldToAutocomplete,
  fieldToTextField,
} from "formik-mui";

import type { PriceOption } from "./types";
import { useGlobalConfig } from "../hooks/useGlobalConfig";

const filter = createFilterOptions({
  matchFrom: "start",
  stringify: (option: any) => option.name,
});

export interface PriceAutocompleteProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> extends AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> {
  prices: PriceOption[];
  label: string;
  required?: boolean;
  // When true, use 'Per Unit' instead of 'Per UOM' in the label suffix
  perUnitSuffix?: boolean;
  clearOnBlur?: boolean;
}

export function ItemPriceAutocomplete<
  // eslint-disable-next-line
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
>(
  props: PriceAutocompleteProps<
    PriceOption,
    Multiple,
    DisableClearable,
    FreeSolo
  >
) {
  const { prices, label, required = false, perUnitSuffix = true, clearOnBlur = true, ...autocompleteProps } = props;
  const { setFieldValue } = autocompleteProps.form;
  const { name } = fieldToTextField(props as any);
  const fieldLabel = useMemo(() => {
    let l = label + (perUnitSuffix ? " (Per Unit)" : " (Per UOM)");
    if (required) {
      return l + "*";
    }
    return l;
  }, [label, required, perUnitSuffix]);
  const globalConfig = useGlobalConfig();

  const customCurrency = useMemo(() => {
    if (prices.length > 0) {
      // splits on whitespace and returns the last element
      return prices[0]["name"].split(" ").slice(-1)[0];
    }
  }, [prices]);

  return (
    <MuiAutocomplete
      {...fieldToAutocomplete(autocompleteProps)}
      fullWidth
      selectOnFocus
      clearOnBlur={clearOnBlur}
      handleHomeEndKeys
      options={prices}
      onChange={(event, newValue) => {
        if (typeof newValue === "string") {
          if (name) {
            const parsedNum = parseFloat(newValue.replace(/[^0-9.-]+/g, ""));
            const customPrice = Number.isFinite(parsedNum)
              ? parsedNum.toFixed(globalConfig?.value_rounding ?? 2)
              : newValue;
            setFieldValue(name, {
              name:
                Number.isFinite(parsedNum) && customCurrency
                  ? `Custom ${customPrice} ${customCurrency}`
                  : newValue,
              value: customPrice,
            });
          }
          // @ts-ignore
        } else if (newValue && newValue.inputValue) {
          // Create a new value from the user input
          if (name) {
            // @ts-ignore
            const customPrice = Number(newValue.inputValue).toFixed(globalConfig?.value_rounding ?? 2)
            setFieldValue(name, {
              // @ts-ignore
              name: `Custom ${customPrice} ${customCurrency}`,
              // @ts-ignore
              value: customPrice,
            });
          }
        } else {
          if (name) {
            setFieldValue(name, newValue);
          }
        }
      }}
      // @ts-ignore
      filterOptions={(options, params) => {
        const filtered = filter(options, params);

        // Suggest the creation of a new value (only when freeSolo allows custom entry)
        if (autocompleteProps.freeSolo !== false && params.inputValue !== "") {
          const customPrice = Number(params.inputValue).toFixed(globalConfig?.value_rounding ?? 2)
          filtered.push({
            inputValue: customPrice,
            name: `Use Custom value "${customPrice}"`,
          });
        }

        return filtered;
      }}
      getOptionLabel={(option) => {
        // Value selected with enter, right from the input
        if (typeof option === "string") {
          return option;
        }
        // Add "xxx" option created dynamically
        if (option.inputValue) {
          return `Custom ${option.inputValue}`;
        }
        // Regular option
        return option.name;
      }}
      renderOption={(props, option) => <li {...props}>{option.name}</li>}
      renderInput={(params) => (
        <MUITextField
          {...params}
          label={fieldLabel}
          error={props.form.touched[name!] && !!props.form.errors[name!]}
          // @ts-ignore
          helperText={props.form.touched[name!] && props.form.errors[name!]}
        />
      )}
    />
  );
}

ItemPriceAutocomplete.displayName = "FormikMaterialUIItemPriceAutocomplete";
